import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded"
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined"
import ChecklistIcon from "@mui/icons-material/Checklist"
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications"
import CodeIcon from "@mui/icons-material/Code"
import EditNoteIcon from "@mui/icons-material/EditNote"
import GroupsIcon from "@mui/icons-material/Groups"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import InsightsIcon from "@mui/icons-material/Insights"
import PowerIcon from "@mui/icons-material/Power"
import { SvgIconProps } from "@mui/material/SvgIcon"
import { ComponentType } from "react"

import { LCTOnlyDatasets } from "../../constants/enums"
import {
    DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT,
    GEN_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT,
} from "../../constants/generalConstants"
import {
    IDENTIFIER_TYPE_INSTALLER_TAG,
    INSTALLER,
    PUBLIC,
    CONFIG_RULE_SOLE_TRADERS_OR_APPROVED_INSTALLER_ONLY,
    INSTALLER_APPROVED,
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    ENA_SYS_ADMIN,
} from "../../constants/roles"
import { routeConfig } from "../../router/appRouter"
import * as routes from "../../router/routes"
import {
    AdminIcon,
    ApprovalsIcon,
    DatabaseIcon,
    EVChargerIcon,
    HeatPumpIcon,
    SolarPanelIcon,
} from "../icons/icons"

/**
 * Filters out the ENA Sys Admin group from the list of groups. Used in cases
 * where sys admin has access to the route but it should not be displayed in
 * the navigation bar
 * @param groups - Array of groups user is in
 * @returns Array of groups user is in, excluding the ENA Sys Admin group
 */
const notSysAdmin = (groups: string[]): string[] =>
    groups.filter((group) => group !== ENA_SYS_ADMIN)

// Group names
const GET_STARTED = "Get Started"
const DATABASES = "Registers"
const FOR_DNOS = "For DNOs"
const FOR_INSTALLERS = "For Installers"
const APPLICATION_MANAGEMENT = "Application Management"
const ACCOUNT = "Account"
const FOR_ADMINS = "For Admins"
const FOR_DEVS = "For Developers"
const FOR_MANUFACTURERS = "For Manufacturer"
const FOR_ASSESSORS = "For Evaluators"
const OTHER = "Other"
const COMPANIES = "Companies"
const FOR_INSIGHTS = "For Analysts"

export type NavOptionType = {
    name: string
    group: string
    icon: ComponentType<SvgIconProps>
    permittedGroups: string[]
    route: string
    onClickSideEffect?: () => void
    external?: boolean
}

export type NavOptionsType = {
    groupOrder: string[]
    options: NavOptionType[]
}

const NAV_OPTIONS: NavOptionsType = {
    groupOrder: [
        GET_STARTED,
        FOR_DNOS,
        APPLICATION_MANAGEMENT,
        DATABASES,
        ACCOUNT,
        FOR_ADMINS,
    ],
    options: [
        // DNO web portal options
        {
            name: "Home",
            group: GET_STARTED,
            icon: HomeOutlinedIcon,
            permittedGroups: routeConfig[routes.BASE_URL].permittedRoles,
            route: routeConfig[routes.BASE_URL].path,
        },
        {
            name: "Awaiting Review",
            group: FOR_DNOS,
            icon: ApprovalsIcon,
            permittedGroups:
                routeConfig[routes.DNO_APPLICATIONS_ESCALATIONS].permittedRoles,
            route: routeConfig[routes.DNO_APPLICATIONS_ESCALATIONS].path,
        },
        {
            name: "All Applications",
            group: FOR_DNOS,
            icon: CircleNotificationsIcon,
            permittedGroups:
                routeConfig[routes.DNO_APPLICATIONS_NOTIFICATIONS]
                    .permittedRoles,
            route: routeConfig[routes.DNO_APPLICATIONS_NOTIFICATIONS].path,
        },
        // Installer
        {
            name: "New Application",
            group: FOR_INSTALLERS,
            icon: EVChargerIcon,
            permittedGroups:
                routeConfig[routes.NEW_SINGLE_APPLICATION].permittedRoles,
            route: routeConfig[routes.NEW_SINGLE_APPLICATION].path,
        },
        {
            name: "My Applications",
            group: FOR_INSTALLERS,
            icon: ChecklistIcon,
            permittedGroups:
                routeConfig[routes.INSTALLER_LIST_APPLICATIONS].permittedRoles,
            route: routeConfig[routes.INSTALLER_LIST_APPLICATIONS].path,
        },
        {
            name: "My Drafts",
            group: FOR_INSTALLERS,
            icon: EditNoteIcon,
            permittedGroups:
                routeConfig[routes.INSTALLER_DRAFTS].permittedRoles,
            route: routeConfig[routes.INSTALLER_DRAFTS].path,
        },
        // Databases
        {
            name: "Live Device Register",
            group: DATABASES,
            icon: DatabaseIcon,
            permittedGroups:
                routeConfig[routes.GEN_DEVICE_SEARCH].permittedRoles,
            route: routeConfig[routes.GEN_DEVICE_SEARCH].path,
        },
        // Manufacturer
        {
            name: "Publish New Device",
            group: FOR_MANUFACTURERS,
            icon: PowerIcon,
            permittedGroups:
                routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].permittedRoles,
            route: routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].path,
        },
        {
            name: "My Generation Devices",
            group: FOR_MANUFACTURERS,
            icon: SolarPanelIcon,
            permittedGroups: notSysAdmin(
                routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].permittedRoles
            ),
            route: `${routes.GEN_DEVICE_SEARCH}`,
        },
        {
            name: "My Heat Pumps",
            group: FOR_MANUFACTURERS,
            icon: HeatPumpIcon,
            permittedGroups: notSysAdmin(
                routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].permittedRoles
            ),
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.HP}`,
        },
        {
            name: "My EVCP ACs",
            group: FOR_MANUFACTURERS,
            icon: EVChargerIcon,
            permittedGroups: notSysAdmin(
                routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].permittedRoles
            ),
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVAC}`,
        },
        {
            name: "My EVCP DCs",
            group: FOR_MANUFACTURERS,
            icon: EVChargerIcon,
            permittedGroups: notSysAdmin(
                routeConfig[routes.PUBLISH_NEW_LCT_TTR_DEVICE].permittedRoles
            ),
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVDC}`,
        },
        // Assessor
        {
            name: "Heat Pumps to Assess",
            group: FOR_ASSESSORS,
            icon: HeatPumpIcon,
            permittedGroups: [DB_ASSESSOR_DEMAND],
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.HP}?${DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`,
        },
        {
            name: "EVCP ACs to Assess",
            group: FOR_ASSESSORS,
            icon: EVChargerIcon,
            permittedGroups: [DB_ASSESSOR_DEMAND],
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVAC}?${DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`,
        },
        {
            name: "EVCP DCs to Assess",
            group: FOR_ASSESSORS,
            icon: EVChargerIcon,
            permittedGroups: [DB_ASSESSOR_DEMAND],
            route: `${routes.DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVDC}?${DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`,
        },
        {
            name: "Devices to Assess",
            group: FOR_ASSESSORS,
            icon: SolarPanelIcon,
            permittedGroups: [DB_ASSESSOR_GEN],
            route: `${routes.GEN_DEVICE_SEARCH}/?${GEN_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`,
        },
        // Insights
        {
            name: "Insights",
            group: FOR_INSIGHTS,
            icon: InsightsIcon,
            permittedGroups: routeConfig[routes.INSIGHTS].permittedRoles,
            route: routeConfig[routes.INSIGHTS].path,
        },
        // Admin
        {
            name: "Manage Users",
            group: FOR_ADMINS,
            icon: AdminIcon,
            permittedGroups: routeConfig[routes.ADMIN_PORTAL].permittedRoles,
            route: routeConfig[routes.ADMIN_PORTAL].path,
        },
        {
            name: "My Organisation",
            group: FOR_ADMINS,
            icon: GroupsIcon,
            permittedGroups:
                routeConfig[routes.ADMIN_MY_ORGANISATION].permittedRoles,
            route: routeConfig[routes.ADMIN_MY_ORGANISATION].path,
        },

        // Developer
        {
            name: "Developer Portal",
            group: FOR_DEVS,
            icon: CodeIcon,
            permittedGroups: routeConfig[routes.DEV_PORTAL].permittedRoles,
            route: routeConfig[routes.DEV_PORTAL].path,
        },
        // Company Search
        {
            name: "Company Search",
            group: COMPANIES,
            icon: AutoStoriesRoundedIcon,
            permittedGroups: routeConfig[routes.COMPANY_SEARCH].permittedRoles,
            route: routeConfig[routes.COMPANY_SEARCH].path,
        },
        // FAQ
        {
            name: "Learn More & FAQs",
            group: OTHER,
            icon: HelpOutlineIcon,
            permittedGroups: routeConfig[routes.LEARN_MORE].permittedRoles,
            route: routeConfig[routes.LEARN_MORE].path,
        },
        {
            name: "Change Log",
            group: OTHER,
            icon: ChangeCircleOutlinedIcon,
            permittedGroups: routeConfig[routes.CHANGE_LOG].permittedRoles,
            route: routeConfig[routes.CHANGE_LOG].path,
        },
    ],
}

/**
 * Returns filtered list of navigation options relevant to user's groups
 * @param {string[]} userGroups - Array of groups users is in
 * @returns {NavOptionsType} - Filtered list of navigation options relevant to
 * user's groups
 */
export const navOptionsByGroup = (userGroups: string[]): NavOptionsType => {
    const groupOrderFiltered = new Set<string>()
    const optionsFiltered = NAV_OPTIONS.options.filter(
        (option: NavOptionType) => {
            // If `permittedGroups` contains `ALL_ALLOWED`, the all users
            // should be presented with this option
            const isAllAllowed = option.permittedGroups.includes(PUBLIC)

            const isInstallerOption = option.permittedGroups.includes(
                CONFIG_RULE_SOLE_TRADERS_OR_APPROVED_INSTALLER_ONLY
            )

            let isValidInstallerConfigRule = false

            if (isInstallerOption) {
                if (
                    userGroups.some((group) =>
                        group.includes(IDENTIFIER_TYPE_INSTALLER_TAG)
                    )
                ) {
                    if (userGroups.includes(INSTALLER_APPROVED)) {
                        isValidInstallerConfigRule = true
                    }
                } else if (userGroups.includes(INSTALLER)) {
                    isValidInstallerConfigRule = true
                }
            }

            const groupMatch = isAllAllowed
                ? true
                : option.permittedGroups.some((group) =>
                      userGroups.includes(group)
                  )
            if (isValidInstallerConfigRule) {
                groupOrderFiltered.add(option.group)
                return true
            }
            if (isAllAllowed || groupMatch) {
                groupOrderFiltered.add(option.group)
                return true
            }
            return false
        }
    )

    return {
        groupOrder: Array.from(groupOrderFiltered),
        options: optionsFiltered,
    }
}
